import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import SkeletonRow from "@/components/SkeletonRow";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listAkreditasi,
  listHubunganKeluarga,
  listKebutuhanKhusus,
  listLokasiKuliah,
  listSize,
} from "@/constants";
import { useDebounce } from "@/hooks/useDebounce";
import useFilePreview from "@/hooks/useFilePreview";
import useSessionStorage from "@/hooks/useSessionStorage";
import { useUser } from "@/hooks/useUser";
import { formatDateIndonesia } from "@/lib/utils";
import {
  getBiodata,
  getListAgama,
  getListAlatTransportasi,
  getListDataPilihanProdi,
  getListJenisTinggal,
  getListNegara,
  getListPerguruanTinggi,
  getListPerguruanTinggiProdi,
  getListWilayah,
} from "@/services/getData";
import {
  Agama,
  AlatTransportasi,
  BiodataSate,
  DataPendaftaran,
  DataSiswa,
  HubunganKeluargaEnum,
  JenisTinggal,
  LokasiKuliah,
  Negara,
  Option,
  PerguruanTinggi,
  PerguruanTinggiProdi,
  Size,
  Wilayah,
} from "@/types";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  Briefcase,
  Contact,
  Contact2,
  GraduationCap,
  MapPin,
  Ruler,
  User,
} from "lucide-react"; // Pastikan install lucide-react atau ganti dengan icon lain
import { SetStateAction, useEffect, useRef, useState } from "react";
import { FaPlus } from "react-icons/fa6";

// Komponen Helper untuk Card agar UI konsisten dan Code lebih bersih
const FormSection = ({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
}) => (
  <div className="bg-white dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm mb-6">
    <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex items-center gap-3">
      <div className="p-2 bg-primary/10 text-primary rounded-lg">
        <Icon size={18} />
      </div>
      <h3 className="font-semibold text-lg">{title}</h3>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

export default function Biodata({
  form,
  setForm,
  error,
  file,
  setFile,
}: {
  form: BiodataSate;
  setForm: React.Dispatch<SetStateAction<BiodataSate>>;
  error: AxiosError<{
    message?: string;
    errors?: Record<string, string[]>;
  }> | null;
  file: File | null;
  setFile: React.Dispatch<SetStateAction<File | null>>;
}) {
  const { user, isPending: isPendingUser } = useUser();
  const preview = useFilePreview(file);
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const uploadRef = useRef<HTMLInputElement | null>(null);
  const [openAsalUniversitas, setOpenAsalUniversitas] = useState(false);
  const [openAsalProdi, setOpenAsalProdi] = useState(false);
  const [openProvinsi, setOpenProvinsi] = useState(false);
  const [openKabupaten, setOpenKabupaten] = useState(false);
  const [openKecamatan, setOpenKecamatan] = useState(false);
  const [openNegara, setOpenNegara] = useState(false);
  const [openAlatTransportasi, setOpenAlatTransportasi] = useState(false);
  const [idFeederProvinsi, setIdFeederProvinsi] = useState("");
  const [idFeederKabupaten, setIdFeederKabupaten] = useState("");

  // State search
  const [searchQueryProvinsi, setSearchQueryProvinsi] = useState("");
  const [searchQueryKabupaten, setSearchQueryKabupaten] = useState("");
  const [searchQueryKecamatan, setSearchQueryKecamatan] = useState("");
  const [searchQueryUniversitas, setSearchQueryUniversitas] = useState("");

  const debouncedQueryUniversitas = useDebounce(searchQueryUniversitas, 1000);

  // --- QUERY HOOKS (Sama seperti logika asli) ---
  const { data: dataPilihanProdi } = useQuery({
    queryKey: ["listDataPilihanProdi"],
    queryFn: () => getListDataPilihanProdi(sessionPeriode?.id_sys_periode),
  });
  const jenjangPilihanProdi = dataPilihanProdi
    ? dataPilihanProdi[0]?.unitkerja?.uk_program
    : "";

  const { data: dataProvinsi, isPending: isPendingProvinsi } = useQuery({
    queryKey: ["listProvinsi", form.provinsi, searchQueryProvinsi],
    queryFn: () =>
      getListWilayah(
        10,
        "1",
        undefined,
        searchQueryProvinsi ? undefined : form.provinsi,
        searchQueryProvinsi,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah?.id_feeder}`,
      })),
  });

  const {
    data: dataKabupaten,
    refetch: refetchKabupaten,
    isPending: isPendingKabupaten,
  } = useQuery({
    queryKey: [
      "listKabupaten",
      idFeederProvinsi,
      form.kabupaten,
      searchQueryKabupaten,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederProvinsi,
        searchQueryKabupaten ? undefined : form.kabupaten,
        searchQueryKabupaten,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah.id_feeder}`,
      })),
    enabled: !!idFeederProvinsi,
  });

  const {
    data: dataKecamatan,
    refetch: refetchKecamatan,
    isPending: isPendingKecamatan,
  } = useQuery({
    queryKey: [
      "listKecamatan",
      idFeederKabupaten,
      searchQueryKecamatan ? undefined : form.kecamatan,
      searchQueryKecamatan,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederKabupaten,
        form.kecamatan,
        searchQueryKecamatan,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
      })),
    enabled: !!idFeederKabupaten,
  });

  const { data: dataNegara } = useQuery({
    queryKey: ["listNegara"],
    queryFn: () => getListNegara(),
    select: (data) =>
      data?.map((negara: Negara) => ({
        label: negara.nama_negara,
        value: `${negara.id_ref_negara}`,
      })),
  });

  const { data: dataAgama } = useQuery({
    queryKey: ["listAgama"],
    queryFn: () => getListAgama(),
  });

  const { data: dataJenisTinggal } = useQuery({
    queryKey: ["listJenisTinggal"],
    queryFn: () => getListJenisTinggal(),
  });

  const selectedJenisTinggal =
    dataJenisTinggal &&
    dataJenisTinggal?.find((item: JenisTinggal) => {
      return item.id_ref_jenis_tinggal == form.id_ref_jenis_tinggal;
    });

  const { data: dataAlatTransportasi } = useQuery({
    queryKey: ["listAlatTransportasi"],
    queryFn: () => getListAlatTransportasi(),
    select: (data) =>
      data?.map((alatTransportasi: AlatTransportasi) => ({
        label: alatTransportasi.nama_alat_transportasi,
        value: `${alatTransportasi.id_ref_alat_transportasi}`,
      })),
  });

  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataBiodata", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getBiodata(sessionPeriode?.id_sys_periode || ""),
      enabled: !!user && !!sessionPeriode?.id_sys_periode,
    });
  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;

  const { data: optionsPt, isPending: isPendingOptionsPt } = useQuery({
    queryKey: ["listPerguruanTinggi", debouncedQueryUniversitas, form.asal_pt],
    queryFn: () =>
      getListPerguruanTinggi(
        "1",
        "10",
        debouncedQueryUniversitas
          ? debouncedQueryUniversitas
          : (form.asal_pt as string),
      ),
    select: (data) => {
      if (!data) return [];
      return data?.map((item: PerguruanTinggi) => ({
        label: item.nama_perguruan_tinggi,
        value: `${item.id_feeder}`,
        list_prodi: item.pt_prodi,
      }));
    },
  });

  const { data: dataAsalProdi } = useQuery({
    queryKey: ["listAsalProdi", form.asal_pt],
    queryFn: () => getListPerguruanTinggiProdi("1", "1000", form.asal_pt || ""),
    select: (data) => {
      return data?.map((item: PerguruanTinggiProdi) => ({
        label: `${item.nama_program_studi} (${item.nama_jenjang_pendidikan})`,
        value: `${item.id_feeder}`,
        nama_jenjang_pendidikan: item.nama_jenjang_pendidikan,
      }));
    },
    enabled: !!form.asal_pt,
  });

  const optionsAsalProdi = dataAsalProdi
    ? [
        ...dataAsalProdi.filter((item: Option) => {
          if (jenjangPilihanProdi === "S2") {
            return (
              item.nama_jenjang_pendidikan === "D4" ||
              item.nama_jenjang_pendidikan === "S1" ||
              item.nama_jenjang_pendidikan === "Sp-1" ||
              item.nama_jenjang_pendidikan === "Profesi"
            );
          }

          if (jenjangPilihanProdi === "S3") {
            return (
              item.nama_jenjang_pendidikan === "S2" ||
              item.nama_jenjang_pendidikan === "Sp-2" ||
              item.nama_jenjang_pendidikan === "S2 Terapan"
            );
          }
          return true;
        }),
        {
          label: "Lainnya",
          value: "LAINNYA",
          nama_jenjang_pendidikan: "Lainnya",
        },
      ]
    : [];

  useEffect(() => {
    if (form.provinsi) refetchKabupaten();
  }, [form.provinsi, refetchKabupaten]);

  useEffect(() => {
    if (form.kabupaten) refetchKecamatan();
  }, [form.kabupaten, refetchKecamatan]);

  useEffect(() => {
    if (!dataSiswa || !sessionPeriode?.id_sys_periode) return;
    setForm((prev) => ({
      ...prev,
      id_sys_periode: sessionPeriode?.id_sys_periode || "",
      ...dataSiswa,
      pilihan_lokasi_kuliah:
        (dataSiswa.data_pendaftaran?.[0]?.data_pilihan_prodi?.[0]?.pilihan_lokasi_kuliah?.toLowerCase() as LokasiKuliah) ??
        "",
      asal_pt: dataPendaftaran?.asal_pt,
      akreditasi_pt_asal: dataPendaftaran?.akreditasi_pt_asal,
      asal_prodi: dataPendaftaran?.asal_prodi,
      asal_prodi_manual: dataPendaftaran?.asal_prodi,
      akreditasi_prodi_asal: dataPendaftaran?.akreditasi_prodi_asal,
      tahun_ijazah_terakhir: dataPendaftaran?.tahun_ijazah_terakhir,
      nilai_IPK: dataPendaftaran?.nilai_IPK,
      is_bekerja: Number(dataPendaftaran?.is_bekerja) === 1 ? true : false,
      tempat_bekerja: dataPendaftaran?.tempat_bekerja,
      penghasilan_bekerja: dataPendaftaran?.penghasilan_bekerja,
    }));
  }, [dataSiswa, dataPendaftaran, setForm, sessionPeriode?.id_sys_periode]);

  useEffect(() => {
    const selectedProvinsi = dataProvinsi?.find(
      (provinsi: Option) => provinsi?.value == form.provinsi,
    );
    if (selectedProvinsi) setIdFeederProvinsi(selectedProvinsi?.id_feeder);

    const selectedKabupaten = dataKabupaten?.find(
      (kabupaten: Option) => kabupaten?.value == form.kabupaten,
    );
    if (selectedKabupaten) setIdFeederKabupaten(selectedKabupaten?.id_feeder);
  }, [
    setIdFeederProvinsi,
    setIdFeederKabupaten,
    dataProvinsi,
    dataKabupaten,
    form,
  ]);

  const isKelasDenpasar =
    dataSiswa?.data_pendaftaran?.[0]?.periode?.sys_prodi?.[0]
      ?.is_kelas_denpasar ?? false;

  return (
    <div className="max-w-7xl mx-auto pb-10">
      <div className="mb-6">
        <h1 className="font-bold text-2xl">Lengkapi Biodata</h1>
        <p className="text-muted-foreground">
          Mohon isi data diri anda dengan benar sesuai dokumen resmi.
        </p>
      </div>

      {isPendingUser || isPendingDataSiswa ? (
        <div className="grid lg:grid-cols-2 grid-cols-1 gap-6">
          {Array(8)
            .fill(null)
            .map((_, index) => (
              <SkeletonRow key={index} />
            ))}
        </div>
      ) : (
        <div className="space-y-6">
          {/* SECTION 1: IDENTITAS DIRI & FOTO */}
          <FormSection title="Identitas Diri" icon={User}>
            <div className="flex flex-col lg:flex-row gap-8">
              {/* KOLOM FOTO - Dibuat Fixed Width agar layout stabil */}
              <div className="flex flex-col items-center justify-start gap-4 flex-shrink-0 w-full lg:w-[240px]">
                <div
                  className="group relative w-[180px] aspect-[3/4] border-2 border-dashed border-zinc-300 rounded-xl overflow-hidden hover:border-primary cursor-pointer transition-all bg-zinc-50 flex items-center justify-center"
                  onClick={(e) => {
                    e.preventDefault(); // FIX: Prevent bubbling/scroll jump
                    if (uploadRef.current) {
                      uploadRef.current.click();
                    }
                  }}
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : form.path_foto ? (
                    <img
                      src={form.path_foto}
                      alt="foto"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-muted-foreground group-hover:text-primary">
                      <FaPlus size={32} className="mb-2" />
                      <p className="text-xs font-semibold">Upload Foto</p>
                    </div>
                  )}

                  {/* Overlay Hint */}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-white text-xs font-medium">
                      Ubah Foto
                    </span>
                  </div>
                </div>

                <input
                  ref={uploadRef}
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  className="hidden"
                  onChange={(event) => {
                    const files = event.target.files;
                    if (files && files[0]) {
                      setFile(files[0]);
                    }
                  }}
                />

                <div className="text-center text-xs text-muted-foreground">
                  <p>{file ? file.name : "Format: JPG, JPEG, PNG"}</p>
                  <InputError
                    text={error?.response?.data?.errors?.foto?.join(",")}
                  />
                </div>
              </div>

              {/* KOLOM INPUT IDENTITAS */}
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
                {isKelasDenpasar && (
                  <div className="md:col-span-2">
                    <Label>Lokasi Kuliah</Label>
                    <Select
                      value={form.pilihan_lokasi_kuliah || ""}
                      onValueChange={(val) =>
                        setForm((prev) => ({
                          ...prev,
                          pilihan_lokasi_kuliah: val as LokasiKuliah,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pilih Lokasi" />
                      </SelectTrigger>
                      <SelectContent>
                        {listLokasiKuliah?.map((v, i) => (
                          <SelectItem key={i} value={v.toLowerCase()}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <InputError
                      text={error?.response?.data?.errors?.pilihan_lokasi_kuliah?.join(
                        ",",
                      )}
                    />
                  </div>
                )}

                <div className="md:col-span-2">
                  <Label required>Nama Lengkap (Tanpa Gelar)</Label>
                  <Input
                    required
                    value={form.nama_lengkap || ""}
                    placeholder="Sesuai ijazah terakhir..."
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        nama_lengkap: e.target.value,
                      }))
                    }
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Sesuai ijazah terakhir.
                  </p>
                  <InputError
                    text={error?.response?.data?.errors?.nama_lengkap?.join(
                      ",",
                    )}
                  />
                </div>

                <div>
                  <Label required>Email</Label>
                  <Input
                    readOnly
                    value={dataSiswa?.email || ""}
                    className="bg-muted"
                  />
                </div>
                <div>
                  <Label required>NIK / KTP</Label>
                  <Input
                    readOnly
                    value={dataSiswa?.nik || ""}
                    className="bg-muted"
                  />
                </div>

                <div>
                  <Label>NISN</Label>
                  <Input
                    maxLength={10}
                    value={form.nisn || ""}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, nisn: e.target.value }))
                    }
                    placeholder="Nomor NISN"
                  />
                  <InputError
                    text={error?.response?.data?.errors?.nisn?.join(",")}
                  />
                </div>

                <div>
                  <Label required>No. Telepon / HP</Label>
                  <Input
                    required
                    value={form.handphone || ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setForm((prev) => ({ ...prev, handphone: val }));
                    }}
                    placeholder="08..."
                  />
                  <InputError
                    text={error?.response?.data?.errors?.handphone?.join(",")}
                  />
                </div>

                <div>
                  <Label required>Tempat Lahir</Label>
                  <Input
                    required
                    value={form.tempat_lahir || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        tempat_lahir: e.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.tempat_lahir?.join(
                      ",",
                    )}
                  />
                </div>

                <div>
                  <Label required>Tanggal Lahir</Label>
                  <Input
                    required
                    type="date"
                    value={form.tanggal_lahir || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        tanggal_lahir: e.target.value,
                      }))
                    }
                  />
                  <p className="text-xs text-primary font-medium mt-1">
                    {form.tanggal_lahir &&
                      formatDateIndonesia(form.tanggal_lahir, "medium")}
                  </p>
                  <InputError
                    text={error?.response?.data?.errors?.tanggal_lahir?.join(
                      ",",
                    )}
                  />
                </div>

                <div>
                  <Label required>Agama</Label>
                  <Select
                    value={String(form.id_ref_agama || "")}
                    onValueChange={(val) =>
                      setForm((prev) => ({ ...prev, id_ref_agama: val }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih Agama">
                        {form.id_ref_agama &&
                          dataAgama?.find(
                            (a: Agama) =>
                              a.id_ref_agama == String(form.id_ref_agama),
                          )?.nama_agama}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {dataAgama?.map((a: Agama, i: number) => (
                        <SelectItem key={i} value={`${a.id_ref_agama}`}>
                          {a.nama_agama}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <InputError
                    text={error?.response?.data?.errors?.id_ref_agama?.join(
                      ",",
                    )}
                  />
                </div>

                <div>
                  <Label required>Jenis Kelamin</Label>
                  <Select
                    value={form.jenis_kelamin || ""}
                    onValueChange={(val) =>
                      setForm((prev) => ({
                        ...prev,
                        jenis_kelamin: val as "L" | "P",
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih Gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L">Laki-laki</SelectItem>
                      <SelectItem value="P">Perempuan</SelectItem>
                    </SelectContent>
                  </Select>
                  <InputError
                    text={error?.response?.data?.errors?.jenis_kelamin?.join(
                      ",",
                    )}
                  />
                </div>

                <div>
                  <Label>NPWP</Label>
                  <Input
                    maxLength={15}
                    value={form.npwp || ""}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, npwp: e.target.value }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.npwp?.join(",")}
                  />
                </div>

                <div>
                  <Label required>Kewarganegaraan</Label>
                  <Combobox
                    open={openNegara}
                    setOpen={setOpenNegara}
                    value={form.id_ref_negara || ""}
                    setValue={(val) =>
                      setForm((prev) => ({ ...prev, id_ref_negara: val }))
                    }
                    options={dataNegara}
                    placeholder="Pilih Negara"
                  />
                  <InputError
                    text={error?.response?.data?.errors?.id_ref_negara?.join(
                      ",",
                    )}
                  />
                </div>
              </div>
            </div>
          </FormSection>

          {/* SECTION 2: ALAMAT */}
          <FormSection title="Alamat Domisili" icon={MapPin}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label required>Alamat (Jalan)</Label>
                <Input
                  required
                  value={form.jalan || ""}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, jalan: e.target.value }))
                  }
                  placeholder="Nama jalan, gang, nomor rumah..."
                />
                <InputError
                  text={error?.response?.data?.errors?.jalan?.join(",")}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>RT</Label>
                  <Input
                    type="number"
                    value={form.rt || ""}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, rt: e.target.value }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.rt?.join(",")}
                  />
                </div>
                <div>
                  <Label>RW</Label>
                  <Input
                    type="number"
                    value={form.rw || ""}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, rw: e.target.value }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.rw?.join(",")}
                  />
                </div>
              </div>

              <div>
                <Label required>Provinsi</Label>
                <Combobox
                  serverSide
                  open={openProvinsi}
                  setOpen={setOpenProvinsi}
                  value={form.provinsi || ""}
                  setValue={(val) => {
                    setForm((prev) => ({
                      ...prev,
                      provinsi: val,
                      kabupaten: "",
                      kecamatan: "",
                    }));
                    const sel = dataProvinsi?.find(
                      (p: Option) => p?.value == val,
                    );
                    setIdFeederProvinsi(sel?.id_feeder);
                  }}
                  options={dataProvinsi}
                  placeholder="Cari Provinsi..."
                  onChange={(e) => setSearchQueryProvinsi(e.target.value)}
                  searchQueryValue={searchQueryProvinsi}
                  isPending={isPendingProvinsi}
                />
                <InputError
                  text={error?.response?.data?.errors?.provinsi?.join(",")}
                />
              </div>

              <div>
                <Label required>Kabupaten/Kota</Label>
                <Combobox
                  serverSide
                  open={openKabupaten}
                  setOpen={setOpenKabupaten}
                  value={form.kabupaten || ""}
                  setValue={(val) => {
                    setForm((prev) => ({
                      ...prev,
                      kabupaten: val,
                      kecamatan: "",
                    }));
                    const sel = dataKabupaten?.find(
                      (k: Option) => k?.value == val,
                    );
                    setIdFeederKabupaten(sel?.id_feeder);
                  }}
                  options={dataKabupaten}
                  placeholder="Cari Kabupaten..."
                  onChange={(e) => setSearchQueryKabupaten(e.target.value)}
                  searchQueryValue={searchQueryKabupaten}
                  isPending={isPendingKabupaten}
                />
                <InputError
                  text={error?.response?.data?.errors?.kabupaten?.join(",")}
                />
              </div>

              <div>
                <Label required>Kecamatan</Label>
                <Combobox
                  serverSide
                  open={openKecamatan}
                  setOpen={setOpenKecamatan}
                  value={form.kecamatan || ""}
                  setValue={(val) =>
                    setForm((prev) => ({ ...prev, kecamatan: val }))
                  }
                  options={dataKecamatan}
                  placeholder="Cari Kecamatan..."
                  onChange={(e) => setSearchQueryKecamatan(e.target.value)}
                  searchQueryValue={searchQueryKecamatan}
                  isPending={isPendingKecamatan}
                />
                <InputError
                  text={error?.response?.data?.errors?.kecamatan?.join(",")}
                />
              </div>

              <div>
                <Label required>Desa/Kelurahan</Label>
                <Input
                  value={form.kelurahan || ""}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, kelurahan: e.target.value }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.kelurahan?.join(",")}
                />
              </div>

              <div>
                <Label required>Kode Pos</Label>
                <Input
                  maxLength={5}
                  value={form.kode_pos || ""}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, kode_pos: e.target.value }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.kode_pos?.join(",")}
                />
              </div>

              <div>
                <Label required>Jenis Tempat Tinggal</Label>
                <Select
                  value={String(form.id_ref_jenis_tinggal || "")}
                  onValueChange={(val) =>
                    setForm((prev) => ({ ...prev, id_ref_jenis_tinggal: val }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih Jenis" />
                  </SelectTrigger>
                  <SelectContent>
                    {dataJenisTinggal?.map((jt: JenisTinggal, i: number) => (
                      <SelectItem key={i} value={`${jt.id_ref_jenis_tinggal}`}>
                        {jt.nama_jenis_tinggal}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.id_ref_jenis_tinggal?.join(
                    ",",
                  )}
                />
              </div>

              {selectedJenisTinggal?.nama_jenis_tinggal.startsWith("Panti") && (
                <div className="md:col-span-2">
                  <Label required>Nama Panti Asuhan</Label>
                  <Input
                    required
                    value={form.nama_panti_asuhan || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        nama_panti_asuhan: e.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.nama_panti_asuhan?.join(
                      ",",
                    )}
                  />
                </div>
              )}
            </div>
          </FormSection>

          {/* SECTION 3: FISIK & LAINNYA */}
          <FormSection title="Data Fisik & Transportasi" icon={Ruler}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
              <div>
                <Label required>Berat Badan (kg)</Label>
                <Input
                  type="number"
                  value={form.berat_badan === 0 ? "" : Number(form.berat_badan)}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      berat_badan: Number(e.target.value),
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.berat_badan?.join(",")}
                />
              </div>
              <div>
                <Label required>Tinggi Badan (cm)</Label>
                <Input
                  type="number"
                  value={
                    form.tinggi_badan === 0 ? "" : Number(form.tinggi_badan)
                  }
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      tinggi_badan: Number(e.target.value),
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.tinggi_badan?.join(",")}
                />
              </div>
              <div>
                <Label required>Ukuran Jas</Label>
                <Select
                  value={form.ukuran_baju || ""}
                  onValueChange={(val) =>
                    setForm((prev) => ({ ...prev, ukuran_baju: val as Size }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih Ukuran" />
                  </SelectTrigger>
                  <SelectContent>
                    {listSize?.map((v, i) => (
                      <SelectItem key={i} value={`${v}`}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.ukuran_baju?.join(",")}
                />
              </div>
              <div>
                <Label required>Alat Transportasi</Label>
                <p className="italic text-muted-foreground text-sm">
                  Isi dengan alat transportasi yang akan digunakan selama kuliah
                </p>
                <Combobox
                  open={openAlatTransportasi}
                  setOpen={setOpenAlatTransportasi}
                  value={form.id_ref_alat_transportasi || ""}
                  setValue={(val) =>
                    setForm((prev) => ({
                      ...prev,
                      id_ref_alat_transportasi: val,
                    }))
                  }
                  options={dataAlatTransportasi}
                  placeholder="Pilih Transportasi"
                />
                <InputError
                  text={error?.response?.data?.errors?.id_ref_alat_transportasi?.join(
                    ",",
                  )}
                />
              </div>
            </div>
          </FormSection>

          {/* SECTION 4: PENDIDIKAN TERAKHIR */}
          <FormSection title="Pendidikan Terakhir" icon={GraduationCap}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label required>
                  Asal Universitas / Institusi{" "}
                  <span className="text-muted-foreground font-normal text-xs">
                    {jenjangPilihanProdi === "S2"
                      ? "(S1/D4)"
                      : jenjangPilihanProdi === "S3"
                        ? "(S2)"
                        : ""}
                  </span>
                </Label>
                <Combobox
                  serverSide
                  open={openAsalUniversitas}
                  setOpen={setOpenAsalUniversitas}
                  value={form.asal_pt || ""}
                  setValue={(val) =>
                    setForm((prev) => ({ ...prev, asal_pt: val }))
                  }
                  onChange={(e) => setSearchQueryUniversitas(e.target.value)}
                  options={optionsPt}
                  placeholder="Cari Kampus..."
                  isPending={isPendingOptionsPt}
                  searchQueryValue={searchQueryUniversitas}
                />
                <InputError
                  text={error?.response?.data?.errors?.asal_pt?.join(",")}
                />
              </div>

              <div>
                <Label required>Akreditasi Kampus</Label>
                <Select
                  value={form.akreditasi_pt_asal || ""}
                  onValueChange={(val) =>
                    setForm((prev) => ({ ...prev, akreditasi_pt_asal: val }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih Akreditasi" />
                  </SelectTrigger>
                  <SelectContent>
                    {listAkreditasi?.map((v, i) => (
                      <SelectItem key={i} value={`${v}`}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.akreditasi_pt_asal?.join(
                    ",",
                  )}
                />
              </div>

              {form.asal_pt && (
                <>
                  <div className="md:col-span-2">
                    <Label required>Program Studi Asal</Label>
                    <Combobox
                      open={openAsalProdi}
                      setOpen={setOpenAsalProdi}
                      value={form.asal_prodi || ""}
                      setValue={(val) =>
                        setForm((prev) => ({ ...prev, asal_prodi: val }))
                      }
                      options={optionsAsalProdi}
                      placeholder="Pilih Prodi..."
                      isPending={isPendingOptionsPt}
                    />
                    <InputError
                      text={error?.response?.data?.errors?.asal_prodi?.join(
                        ",",
                      )}
                    />
                  </div>

                  {form.asal_prodi == "LAINNYA" && (
                    <div className="md:col-span-2">
                      <Label>Nama Program Studi (Manual)</Label>
                      <Input
                        value={form.asal_prodi_manual}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            asal_prodi_manual: e.target.value,
                          }))
                        }
                        placeholder="Ketik nama program studi..."
                      />
                    </div>
                  )}

                  <div>
                    <Label required>Akreditasi Prodi</Label>
                    <Select
                      value={form.akreditasi_prodi_asal || ""}
                      onValueChange={(val) =>
                        setForm((prev) => ({
                          ...prev,
                          akreditasi_prodi_asal: val,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pilih Akreditasi" />
                      </SelectTrigger>
                      <SelectContent>
                        {listAkreditasi?.map((v, i) => (
                          <SelectItem key={i} value={`${v}`}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <InputError
                      text={error?.response?.data?.errors?.akreditasi_prodi_asal?.join(
                        ",",
                      )}
                    />
                  </div>
                </>
              )}

              <div>
                <Label required>Tahun Lulus (Ijazah)</Label>
                <Input
                  minLength={4}
                  maxLength={4}
                  value={form.tahun_ijazah_terakhir || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      tahun_ijazah_terakhir: e.target.value,
                    }))
                  }
                  placeholder="YYYY"
                />
                <InputError
                  text={error?.response?.data?.errors?.tahun_ijazah_terakhir?.join(
                    ",",
                  )}
                />
              </div>

              <div>
                <Label required>Nilai IPK</Label>
                <Input
                  type="number"
                  value={form.nilai_IPK || ""}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, nilai_IPK: e.target.value }))
                  }
                  placeholder="Contoh: 3.50"
                />
                <InputError
                  text={error?.response?.data?.errors?.nilai_IPK?.join(",")}
                />
              </div>
            </div>
          </FormSection>

          {/* SECTION 5: KELUARGA & PEKERJAAN */}
          <FormSection title="Keluarga & Pekerjaan" icon={Briefcase}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label required>Nama Ibu Kandung</Label>
                <Input
                  required
                  value={form.nama_ibu_kandung || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      nama_ibu_kandung: e.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.nama_ibu_kandung?.join(
                    ",",
                  )}
                />
              </div>

              <div className="md:col-span-2">
                <Label required>Status Bekerja</Label>
                <div className="mt-2 flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={form.is_bekerja === false}
                      onCheckedChange={(c) =>
                        setForm((prev) => ({
                          ...prev,
                          is_bekerja: c ? false : undefined,
                        }))
                      }
                    />
                    <span className="text-sm">Belum Bekerja</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={form.is_bekerja === true}
                      onCheckedChange={(c) =>
                        setForm((prev) => ({
                          ...prev,
                          is_bekerja: c ? true : undefined,
                        }))
                      }
                    />
                    <span className="text-sm">Sudah Bekerja</span>
                  </div>
                </div>
                <InputError
                  text={error?.response?.data?.errors?.is_bekerja?.join(",")}
                />
              </div>

              {form.is_bekerja && (
                <>
                  <div>
                    <Label>Instansi Bekerja</Label>
                    <Input
                      value={form.tempat_bekerja || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          tempat_bekerja: e.target.value,
                        }))
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.tempat_bekerja?.join(
                        ",",
                      )}
                    />
                  </div>
                  <div>
                    <Label>Penghasilan Kotor (Rp)</Label>
                    <Input
                      type="number"
                      value={form.penghasilan_bekerja || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          penghasilan_bekerja: e.target.value,
                        }))
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.penghasilan_bekerja?.join(
                        ",",
                      )}
                    />
                  </div>
                </>
              )}
            </div>
          </FormSection>

          <FormSection title="Kontak Darurat" icon={Contact2}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
              <div className="md:col-span-2">
                <Label required>Hubungan Kontak Darurat</Label>
                <Select
                  value={form.kontak_darurat_hubungan || ""}
                  onValueChange={(value) => {
                    if (!value) return;
                    setForm((prev) => ({
                      ...prev,
                      kontak_darurat_hubungan: value as HubunganKeluargaEnum,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih hubungan kontak darurat" />
                  </SelectTrigger>
                  <SelectContent>
                    {listHubunganKeluarga?.map(
                      (option: Option, index: number) => (
                        <SelectItem key={index} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.kontak_darurat_hubungan?.join(
                    ",",
                  )}
                />
              </div>

              <div>
                <Label required>Nama Kontak Darurat</Label>
                <Input
                  required
                  placeholder="Nama kontak darurat..."
                  value={form.kontak_darurat_nama || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      kontak_darurat_nama: e.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.kontak_darurat_nama?.join(
                    ",",
                  )}
                />
              </div>

              <div>
                <Label>Nomor Handphone Kontak Darurat</Label>
                <Input
                  required
                  maxLength={15}
                  value={form.kontak_darurat_hp || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      kontak_darurat_hp: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  placeholder="08xxxxxxxxxx"
                  inputMode="numeric"
                />
                <InputError
                  text={error?.response?.data?.errors?.kontak_darurat_hp?.join(
                    ",",
                  )}
                />
              </div>
            </div>
          </FormSection>

          {/* SECTION 6: KEBUTUHAN KHUSUS */}
          <FormSection title="Kebutuhan Khusus" icon={Contact}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {listKebutuhanKhusus.map((value, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <Checkbox
                    checked={form?.kebutuhan_khusus?.includes(value) || false}
                    onCheckedChange={(isChecked) => {
                      setForm((prev) => {
                        const updated = isChecked
                          ? [...(prev.kebutuhan_khusus || []), value]
                          : (prev.kebutuhan_khusus || []).filter(
                              (item) => item !== value,
                            );
                        return { ...prev, kebutuhan_khusus: updated };
                      });
                    }}
                  />
                  <span className="text-sm font-medium">{value}</span>
                </div>
              ))}
            </div>
          </FormSection>
        </div>
      )}
    </div>
  );
}
